import React from 'react';
import { Award, Shield, Users, Leaf } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/mock';

export const About = () => {
  const { language } = useLanguage();
  const t = translations[language].about;

  const icons = [Award, Shield, Users, Leaf];

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl sm:text-5xl font-black text-slate-800 mb-4">            {t.title}{' '}
            <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
              {t.titleGradient}
            </span>
          </h2>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Text Content */}
          <div className="space-y-6">
            <p className="text-lg text-slate-700 leading-relaxed">
              {t.description}
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              {t.mission}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 pt-6">
              <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-none">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-black text-blue-600">{t.stats.established}</p>
                  <p className="text-xs text-slate-600 mt-1">{language === 'en' ? 'Established' : 'Didirikan'}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-none">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-black text-purple-600">{t.stats.staff}</p>
                  <p className="text-xs text-slate-600 mt-1">{language === 'en' ? 'Team' : 'Tim'}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-pink-50 to-rose-50 border-none">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-black text-rose-600">{t.stats.production}</p>
                  <p className="text-xs text-slate-600 mt-1">{language === 'en' ? 'Capacity' : 'Kapasitas'}</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Image with Badges */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="https://bahteralimautama.com/image/pabrik1.jpeg"
                alt="Factory"
                className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-2 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              
              {/* Floating Badges */}
              <div className="absolute top-6 right-6 animate-float">
                <Badge className="px-4 py-2 bg-white/90 backdrop-blur-md text-blue-600 font-bold text-sm shadow-xl">
                  {t.badge1}
                </Badge>
              </div>
              <div className="absolute bottom-6 left-6 animate-float" style={{ animationDelay: '1s' }}>
                <Badge className="px-4 py-2 bg-white/90 backdrop-blur-md text-purple-600 font-bold text-sm shadow-xl">
                  {t.badge2}
                </Badge>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl" />
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.values.map((value, index) => {
            const Icon = icons[index];
            return (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-none bg-gradient-to-br from-slate-50 to-blue-50"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-lg text-slate-800 mb-2">{value.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
